import { Head, Link } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { Badge } from "@/components/ui/badge"

type DonationDetail = {
  id: number
  user_id: number
  user_email: string
  user_name: string
  tier: string
  amount_cents: number
  currency: string
  stripe_payment_id: string
  display_publicly: boolean
  donated_at: string
  updated_at: string
}

export default function AdminDonationShow({ donation: d }: { donation: DonationDetail }) {
  return (
    <>
      <Head title={`Donation #${d.id} — Admin`}>
        <meta name="description" content={`Donation detail for ${d.user_email}.`} />
        <meta property="og:title" content={`Donation #${d.id} — Admin`} />
        <meta property="og:description" content={`Donation detail for ${d.user_email}.`} />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1>Donation #{d.id}</h1>
              <p className="mt-1">{d.user_name || d.user_email}</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge tone={d.tier === "lifetime" ? "accent" : "signal"}>
                {d.tier === "lifetime" ? "Lifetime" : "Annual"}
              </Badge>
              <Link
                href="/admin/donations"
                className="text-sm text-ink-muted no-underline hover:text-ink-display"
              >
                Back to donations
              </Link>
            </div>
          </div>
        </div>

        <dl className="mt-6 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-[max-content_1fr]">
          <DetailRow label="Donor" value={d.user_name || "—"} />
          <dt className="text-sm text-ink-muted">User</dt>
          <dd className="text-sm text-ink-body">
            <Link href={`/admin/users/${d.user_id}`} className="underline">
              {d.user_email}
            </Link>
          </dd>
          <DetailRow label="Amount" value={`$${(d.amount_cents / 100).toFixed(2)} ${d.currency}`} />
          <DetailRow label="Tier" value={d.tier === "lifetime" ? "Lifetime" : "Annual"} />
          <DetailRow label="Stripe payment ID" value={d.stripe_payment_id} />
          <DetailRow label="Publicly listed" value={d.display_publicly ? "Yes" : "No"} />
          <DetailRow label="Donated" value={formatDateTime(d.donated_at)} />
          <DetailRow label="Updated" value={formatDateTime(d.updated_at)} />
        </dl>
      </AdminShell>
    </>
  )
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-sm text-ink-muted">{label}</dt>
      <dd className="text-sm text-ink-body">{value}</dd>
    </>
  )
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}
